import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Dict } from 'mixpanel-browser';
import { forkJoin, Observable, of as observableOf } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap, filter } from 'rxjs/operators';

import { Provider_patient_careTeam_pcp } from '@app/account/my-provider/__generated__/Provider';
import { ProviderService } from '@app/account/my-provider/provider-graphql.service';
import { State } from '@app/app.reducer';
import {
  fluAndCovidAppointmentTypeIds,
  MENTAL_HEALTH_APPOINTMENT_BOOKED,
  MENTAL_HEALTH_ONLY_TOPICS_APPOINTMENT_BOOKED,
  mentalHealthAppointmentTypes,
  NOT_COVID_OR_FLU_APPOINTMENT_BOOKED,
  onlyMentalHealthTopicAppointmentTypesFromBhxExperiment,
} from '@app/appointment/appointment-type-experiment-data';
import { ANALYTICS_TIMESTAMP_FORMAT } from '@app/core/analytics.service';
import {
  AppointmentAnalyticsBaseService,
  AppointmentAnalyticsProperty,
} from '@app/core/appointment-analytics-base.service';
import { FeatureFlags, FeatureFlagVariants, FeatureFlagVariantsWithOn } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import {
  APPOINTMENT_CANCEL_CLICKED,
  APPOINTMENT_CANCELLED,
  APPOINTMENT_RESCHEDULE_CLICKED,
  EVENT_BOOK_VISIT_CLICKED,
  EVENT_RECOMMENDED_SERVICES_CARD_CLICKED,
  EVENT_SCHEDULE_VISIT_CLICKED,
  EXPLORE_OTHER_APPOINTMENTS_CLICKED,
  FLOW_APPOINTMENT_BOOKING,
  MODULE_HOME_PAGE,
  MODULE_MENTAL_HEALTH_TOPIC_ROUTING_PAGE,
  MP_EVENT_PAGE_VIEWED,
  MP_EVENT_ADD_TO_CALENDAR_CLICKED,
  MP_EVENT_BANNER_ADD_TO_CALENDAR_CLICKED,
  MP_EVENT_SELF_PAY_CLICKED,
  MP_EVENT_SELF_PAY_ESTIMATE_DISPLAYED,
  MP_EVENT_APPOINTMENT_SELECTED,
  MP_EVENT_REVIEW_BOOKING_CLICKED,
  MP_EVENT_ADD_OR_REPLACE_INSURANCE_CLICKED,
  MP_EVENT_INSURANCE_CARRIER_SELECTED,
  MP_EVENT_CONTINUE_CLICKED,
  MP_EVENT_BACK_CLICKED,
  MP_EVENT_ACTION_DEFERRED,
  MP_EVENT_ADD_OR_REPLACE_PAYMENT_METHOD_CLICKED,
  MP_EVENT_MODAL_DISPLAYED,
  EXPERIMENT_VARIATION_ASSIGNED,
  FLOW_GET_CARE,
} from '@app/core/mixpanel.constants';
import { MixpanelService } from '@app/core/mixpanel.service';
import { UserService } from '@app/core/user.service';
import { AppointmentBookingSource } from '@app/shared/appointment-booking-source';
import { formatDate } from '@app/shared/date-format.pipe';
import { InsuranceCaptureService } from '@app/shared/insurance-capture/insurance-capture.service';
import { PaymentCaptureService } from '@app/shared/payment-capture/payment-capture.service';

import { SurveyQuestion } from '../survey/survey-models';
import { ServiceArea } from './../shared/service-area';
import { AppointmentAnalyticsTypeInfoGraphQLService } from './appointment-analytics-type-info-graphql.service';
import { AppointmentBookingState } from './appointment-booking-state-service/appointment-booking-state';
import { AppointmentSearchState } from './appointment-search-service/appointment-search-state';
import { AppointmentType, MENTAL_HEALTH_VISIT_APPT_TYPE_ID } from './appointment-type';
import { PrimaryInsuranceAndMembershipIsDpcGraphQLService } from './primary-insurance-and-membership-is-dpc-graphql.service';
import { AppointmentInventory } from './provider-inventories';
import { BiosInBookingExperiment } from './search-results/provider-detail/provider-detail.component';

export enum TrackableFilterType {
  DATE = 'date',
  LOCATION = 'location',
  APPOINTMENT_VISIT_FILTER_TOGGLE = 'Appointment Type Toggle',
}

export interface AppointmentJoinPageViewedProperties {
  appointment_id: string;
  module_variant: 'success' | 'error';
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentAnalyticsService extends AppointmentAnalyticsBaseService {
  protected bookingState: AppointmentBookingState;
  protected searchState: AppointmentSearchState;
  private bookingStartedParams?: { module: AppointmentAnalyticsProperty; flow: AppointmentAnalyticsProperty };

  constructor(
    private appointmentAnalyticsTypeInfoGraphQLService: AppointmentAnalyticsTypeInfoGraphQLService,
    mixpanel: MixpanelService,
    store: Store<State>,
    launchDarkly: LaunchDarklyService,
    primaryInsuranceAndMembershipIsDpcGraphQLService: PrimaryInsuranceAndMembershipIsDpcGraphQLService,
    private route: ActivatedRoute,
    private insuranceCaptureService: InsuranceCaptureService,
    private paymentCaptureService: PaymentCaptureService,
    private userService: UserService,
    private providerService: ProviderService,
  ) {
    super(mixpanel, store, launchDarkly, primaryInsuranceAndMembershipIsDpcGraphQLService);
  }

  bookingStarted() {
    return this.trackWithBookingAndSearchStates('Appointment Booking Started', this.bookingStartedParams);
  }

  reasonSubmitted({
    structuredRFVSelected = false,
    reasonInSuggestedList = false,
  }: {
    structuredRFVSelected: boolean;
    reasonInSuggestedList?: boolean;
  }) {
    return this.trackWithBookingAndSearchStates('Appointment Reason Submitted', {
      structured_rfv_selected: structuredRFVSelected,
      reason_in_suggested_list: reasonInSuggestedList,
    });
  }

  bookingPageViewed(pageName: string, properties: object = {}) {
    return this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.BookingPageModule,
      submodule: pageName,
      source: this.route.snapshot.queryParams.source,
      ...properties,
    });
  }

  reschedulePageViewed() {
    return this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, {
      flow: AppointmentAnalyticsProperty.RescheduleFlow,
      module: AppointmentAnalyticsProperty.ReasonForReschedulePageModule,
      source: this.route.snapshot.queryParams.source,
    });
  }

  appointmentJoinPageViewed(properties: AppointmentJoinPageViewedProperties) {
    return this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      module: AppointmentAnalyticsProperty.AppointmentJoinModule,
      // TODO: consult with PAWS and add flow property
      // flow:
      ...properties,
    });
  }

  searched() {
    this.trackLaunchDarklyDeepLinkInventoryPageViewed(); // BHX team tracking experiment
    return this.trackWithBookingAndSearchStates('Appointment Inventory Searched', {
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      appointment_type: this.searchState.appointmentType.displayName,
      appointment_type_id: this.searchState.appointmentType.id,
    });
  }

  searchFailed() {
    return this.trackWithBookingAndSearchStates('Appointment Inventory Search Failed');
  }

  appointmentTypeSelected(appointmentType: AppointmentType) {
    return this.trackWithBookingAndSearchStates('Appointment Type Selected', {
      appointment_type_id: appointmentType.id,
      appointment_type: appointmentType.displayName,
      flow: FLOW_APPOINTMENT_BOOKING,
    });
  }

  inventorySelected(
    inventory: AppointmentInventory,
    props?: { isRemoteRecommendation?: boolean; appointmentTypeIsBillable?: boolean; isRemote: boolean },
  ): void {
    const properties: any = {
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      appointment_inventory_id: inventory.id,
    };

    if (props?.isRemoteRecommendation) {
      properties.submodule = AppointmentAnalyticsProperty.NextAvailableRemoteVisitSubmodule;
    }

    if (!props?.appointmentTypeIsBillable) {
      this.trackWithBookingAndSearchStates(MP_EVENT_APPOINTMENT_SELECTED, properties);
      return;
    }

    forkJoin([
      this.trackInventorySelectedWithExperiment(properties, props?.isRemote || false).pipe(take(1)),
      this.trackPaymentCaptureM3ExperimentVariationAssignedInventory().pipe(take(1)),
      this.trackInsuranceCaptureRequiredExperimentVariationAssigned().pipe(take(1)),
    ]).subscribe();
  }

  seeMoreAppointmentsClicked(inventoryCount: number, props: { newlyDisplayedInventoryIds: number[] }) {
    const trackProps = {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      newly_displayed_inventory_count: inventoryCount,
      newly_displayed_inventory_ids: props.newlyDisplayedInventoryIds,
    };
    return this.trackWithBookingAndSearchStates('Show More Appointments Clicked', trackProps);
  }

  filterApplied(filterType: string, filterDetails: object) {
    const properties = {
      filter_type: filterType,
      ...filterDetails,
    };
    return this.trackWithBookingAndSearchStates('Filter Applied', properties);
  }

  paginateNext() {
    return this.trackWithBookingAndSearchStates('Next Button Clicked');
  }

  paginatePrevious() {
    return this.trackWithBookingAndSearchStates('Previous Button Clicked');
  }

  bookAppointmentClicked(inventory: AppointmentInventory) {
    const trackProps = {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      appointment_inventory_id: inventory.id,
      selected_date: formatDate(inventory.start_time, ANALYTICS_TIMESTAMP_FORMAT, inventory.timezone),
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      submodule: AppointmentAnalyticsProperty.SelectedAppointmentBookingModal,
    };
    return this.trackWithBookingAndSearchStates('Book Appointment Clicked', trackProps);
  }

  bookingCancelled() {
    return this.trackWithBookingAndSearchStates('Appointment Booking Cancelled');
  }

  private getPcpId$(): Observable<string | null> {
    return this.providerService.fetch().pipe(
      map(result => {
        const pcp: Provider_patient_careTeam_pcp | null = result.data.patient.careTeam.pcp;
        if (!pcp) {
          return null;
        }
        return pcp.id;
      }),
      catchError(() => observableOf(null)),
    );
  }

  appointmentBooked(appointment_id: number, inventory_id: number, appointment_type_id: number) {
    const provider = this.bookingState.inventoryProvider;
    const serviceArea = this.bookingState.getSelectedServiceArea();

    const baseProperties: Dict = {
      module: AppointmentAnalyticsProperty.AppointmentConfirmationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      appointment_id,
      appointment_type_id,
      appointment_inventory_id: inventory_id,
    };

    const tracking$ = this.getPcpId$().pipe(
      map(pcpProviderId => this.getOmniMsaSearchProperties(provider, pcpProviderId, serviceArea, baseProperties)),
      switchMap(properties => {
        this.trackLaunchDarklyAppointmentBooked(appointment_type_id);
        return this.trackWithBookingAndSearchStates('Appointment Booked', properties);
      }),
      shareReplay(1),
    );
    tracking$.subscribe();
    return tracking$;
  }

  getOmniMsaSearchProperties(
    provider: any,
    pcpProviderId: string | null,
    serviceArea: ServiceArea,
    baseProperties: Dict,
  ): Dict {
    const isPcp = provider && pcpProviderId && String(provider?.id) === String(pcpProviderId);
    return {
      ...baseProperties,
      provider_id: provider?.id,
      provider_name: provider?.displayName,
      service_area: serviceArea?.name,
      is_ccp: provider?.isCrossCredentialed || false,
      is_pcp: isPcp,
    };
  }

  jumpAheadClicked() {
    return this.trackWithBookingAndSearchStates('Jump Ahead Clicked', {
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
    });
  }

  addToCalendarClicked() {
    const properties: any = {
      module: AppointmentAnalyticsProperty.AppointmentConfirmationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
    };

    return this.trackWithBookingAndSearchStates(MP_EVENT_ADD_TO_CALENDAR_CLICKED, properties);
  }

  trackBannerAddToCalendarClicked() {
    return this.trackWithBookingAndSearchStates(MP_EVENT_BANNER_ADD_TO_CALENDAR_CLICKED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentConfirmationPageModule,
    });
  }

  appointmentSurveySubmitted(surveyName: string) {
    return this.trackWithBookingAndSearchStates('Survey Submitted', {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.BookingPageModule,
      submodule: surveyName,
    });
  }

  appointmentSurveyQuestionAnswered(surveyName: string, surveyQuestion: SurveyQuestion) {
    return this.trackWithBookingAndSearchStates('Survey Question Answered', {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.BookingPageModule,
      submodule: surveyName,
      survey_question: surveyQuestion.questionText,
      survey_answer: surveyQuestion.patientAnswers.map(answer => answer.answerText).join(', '),
    });
  }

  reasonForCancelSubmitted() {
    return this.trackWithBookingAndSearchStates('Reason for Cancel Submitted', {
      flow: AppointmentAnalyticsProperty.RescheduleFlow,
      module: AppointmentAnalyticsProperty.ReasonForReschedulePageModule,
    });
  }

  rescheduled() {
    return this.trackWithBookingAndSearchStates('Appointment Rescheduled', {
      flow: AppointmentAnalyticsProperty.RescheduleFlow,
      module: AppointmentAnalyticsProperty.AppointmentConfirmationPageModule,
    });
  }

  trackOMNowPageViewed() {
    return this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: AppointmentAnalyticsProperty.OMNowNavigationFlow,
      module: AppointmentAnalyticsProperty.VirtualOnlyLandingPageModule,
    });
  }

  trackJoinVideoVisitClicked() {
    return this.trackWithDefaultProperties('Send Link Clicked', {
      flow: AppointmentAnalyticsProperty.OMNowNavigationFlow,
      module: AppointmentAnalyticsProperty.VirtualOnlyLandingPageModule,
      submodule: AppointmentAnalyticsProperty.GetVirtualCareCardSubmodule,
    });
  }

  trackJoinRemoteVisitClicked(appointmentId: string) {
    return this.trackWithDefaultProperties('Remote Visit Started', {
      module: AppointmentAnalyticsProperty.AppointmentJoinModule,
      flow: FLOW_APPOINTMENT_BOOKING,
      appointment_id: appointmentId,
    });
  }

  trackMessageAProviderClicked() {
    return this.trackWithDefaultProperties('Message A Provider Clicked', {
      flow: AppointmentAnalyticsProperty.OMNowNavigationFlow,
      module: AppointmentAnalyticsProperty.VirtualOnlyLandingPageModule,
      submodule: AppointmentAnalyticsProperty.MessageProviderCardSubmodule,
    });
  }

  trackBookVisitClicked() {
    return this.trackWithDefaultProperties(EVENT_BOOK_VISIT_CLICKED, {
      flow: AppointmentAnalyticsProperty.OMNowNavigationFlow,
      module: AppointmentAnalyticsProperty.VirtualOnlyLandingPageModule,
      submodule: AppointmentAnalyticsProperty.GetCareCardSubmodule,
    });
  }

  trackContinueToBookingClicked(
    { name: service_area_selected }: ServiceArea,
    module: AppointmentAnalyticsProperty | string,
    submodule: AppointmentAnalyticsProperty | string,
  ) {
    return this.trackWithDefaultProperties('Continue To Booking Clicked', {
      service_area_selected,
      module,
      submodule,
      flow: AppointmentAnalyticsProperty.OMNowNavigationFlow,
    });
  }

  trackGetCareLinkClicked({
    topicName,
    module,
    flow_version = null,
    source = null,
  }: {
    topicName: string;
    module: string;
    source?: string;
    flow_version?: string;
  }) {
    const mixpanelAttributes: {
      topic_name: string;
      flow: string;
      module: string;
      flow_version?: string;
      source?: string;
    } = {
      topic_name: topicName,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: module,
    };
    if (source !== null && source !== '') {
      mixpanelAttributes.source = source;
    }
    if (flow_version !== null && flow_version !== '') {
      mixpanelAttributes.flow_version = flow_version;
    }
    this.trackWithBookingAndSearchStates('Get Care Link Clicked', mixpanelAttributes);
  }

  /** BHX Program Ineligibility Events */

  // Sets the properties to pass to `.bookingStarted` to track what module/flow is being referred from
  setAppointmentBookingFromBehavioralHealthProgramIneligibility() {
    this.bookingStartedParams = {
      module: AppointmentAnalyticsProperty.BehavioralHealthProgramIneligiblePageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
    };
  }

  behavioralHealthProgramIneligibilityPageViewed() {
    return this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, {
      module: AppointmentAnalyticsProperty.BehavioralHealthProgramIneligiblePageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
    });
  }

  behavioralHealthProgramIneligibilityMessageUsClicked() {
    return this.trackWithDefaultProperties('Message Us Clicked', {
      module: AppointmentAnalyticsProperty.BehavioralHealthProgramIneligiblePageModule,
      flow: AppointmentAnalyticsProperty.MemberRequestFlow,
    });
  }
  /** End BHX Program Ineligibility Events */

  /** Mindset Events */
  mindsetEducationPageViewed(source?: string) {
    return this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, {
      module: AppointmentAnalyticsProperty.MindsetEducationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      source,
    });
  }

  mindsetEducationAppointmentBookingStarted() {
    return this.trackWithBookingAndSearchStates('Appointment Booking Started', {
      module: AppointmentAnalyticsProperty.MindsetEducationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
    });
  }
  /** End Mindset Events */

  /** Shift Events */
  shiftEducationPageViewed(options: string[], source?: string) {
    return this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, {
      module: AppointmentAnalyticsProperty.ShiftEducationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      source: source,
      option_count: options.length,
      options: options,
    });
  }

  shiftEducationAppointmentBookingStarted(appointmentTypeName: string) {
    return this.trackWithBookingAndSearchStates('Appointment Booking Started', {
      module: AppointmentAnalyticsProperty.ShiftEducationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      appointment_type_name: appointmentTypeName,
    });
  }

  shiftSeeAllOptionsClicked(optionCount: number) {
    return this.trackWithBookingAndSearchStates('See Options Clicked', {
      module: AppointmentAnalyticsProperty.ShiftEducationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      option_count: optionCount,
    });
  }

  shiftCarouselInteraction(interaction: string) {
    return this.trackWithBookingAndSearchStates('Carousel Clicked', {
      module: AppointmentAnalyticsProperty.ShiftEducationPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      submodule: interaction,
    });
  }
  /** End Shift Events */

  trackRescheduleStarted(appointmentId: string) {
    super.trackWithDefaultProperties('Appointment Reschedule Started', {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentsPageModule,
      appointment_id: appointmentId,
    });
  }

  trackAppointmentCancellationStarted(appointmentId: string) {
    this.appointmentTypeDisplayName$(appointmentId)
      .pipe(
        switchMap(appointmentTypeDisplayName =>
          this.trackWithDefaultProperties(APPOINTMENT_CANCEL_CLICKED, {
            flow: FLOW_APPOINTMENT_BOOKING,
            module: AppointmentAnalyticsProperty.AppointmentsPageModule,
            appointment_id: appointmentId,
            appointment_type: appointmentTypeDisplayName,
          }),
        ),
      )
      .subscribe();
  }

  trackAppointmentCanceled(appointmentId: string) {
    super.trackWithDefaultProperties(APPOINTMENT_CANCELLED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentsPageModule,
      appointment_id: appointmentId,
    });
  }

  trackAppointmentRescheduleStarted(appointmentId: string) {
    this.appointmentTypeDisplayName$(appointmentId)
      .pipe(
        switchMap(appointmentTypeDisplayName =>
          this.trackWithDefaultProperties(APPOINTMENT_RESCHEDULE_CLICKED, {
            flow: FLOW_APPOINTMENT_BOOKING,
            module: AppointmentAnalyticsProperty.AppointmentsPageModule,
            appointment_id: appointmentId,
            appointment_type: appointmentTypeDisplayName,
          }),
        ),
      )
      .subscribe();
  }

  trackMarkedAsRunningLateToAppointment(appointmentId: string) {
    super.trackWithDefaultProperties('Appointment Marked As Running Late', {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentsPageModule,
      appointment_id: appointmentId,
    });
  }

  trackWaitlistShown() {
    this.trackWithBookingAndSearchStates('Inventory Waitlist Shown', {
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
    });
  }

  trackWaitlistJoined(memberId: number, state: string, b2bCompanyName: string) {
    this.trackWithBookingAndSearchStates('Inventory Waitlist Joined', {
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      flow: AppointmentAnalyticsProperty.BookingFlow,
      address_state: state,
      patient_id: memberId,
      b2b_company_name: b2bCompanyName,
    });
  }

  trackInventorySearchViewed() {
    this.launchDarklyService
      .featureFlag$(FeatureFlags.BHX_PROVIDER_BIOS_IN_BOOKING_EXPERIMENT, BiosInBookingExperiment.Off)
      .pipe(
        map(variationName => {
          const properties: Dict = {
            flow: AppointmentAnalyticsProperty.BookingFlow,
            module: AppointmentAnalyticsProperty.InventoryPageModule,
          };

          if (variationName !== BiosInBookingExperiment.Off) {
            properties.experiment_name = FeatureFlags.BHX_PROVIDER_BIOS_IN_BOOKING_EXPERIMENT;
            properties.experiment_variation_name = variationName;
          }

          return properties;
        }),
        take(1),
      )
      .subscribe(properties => {
        this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, properties);
      });
  }

  trackRecommendedServiceCardClicked(properties: Dict = {}) {
    return this.trackWithDefaultProperties(EVENT_RECOMMENDED_SERVICES_CARD_CLICKED, {
      flow: FLOW_APPOINTMENT_BOOKING,
      submodule: 'Mental Health Card',
      module: MODULE_HOME_PAGE,
      click_type: 'See Programs',
      ...properties,
    });
  }

  trackMentalHealthRoutingPageViewed(topics: string[]) {
    return this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: FLOW_APPOINTMENT_BOOKING,
      module: MODULE_MENTAL_HEALTH_TOPIC_ROUTING_PAGE,
      source: MODULE_HOME_PAGE,
      topics_displayed: topics,
    });
  }

  trackMentalHealthRoutingProgramSelected(cardTitle: string) {
    return this.trackWithDefaultProperties(EVENT_SCHEDULE_VISIT_CLICKED, {
      flow: FLOW_APPOINTMENT_BOOKING,
      module: MODULE_MENTAL_HEALTH_TOPIC_ROUTING_PAGE,
      submodule: cardTitle,
    });
  }

  trackMentalHealthRoutingExploreOtherProgramsSelected() {
    return this.trackWithDefaultProperties(EXPLORE_OTHER_APPOINTMENTS_CLICKED, {
      flow: FLOW_APPOINTMENT_BOOKING,
      module: MODULE_MENTAL_HEALTH_TOPIC_ROUTING_PAGE,
    });
  }

  trackUpdateInsuranceModalOpened() {
    return this.trackWithDefaultProperties(MP_EVENT_ADD_OR_REPLACE_INSURANCE_CLICKED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentSelectedPage,
    });
  }

  trackAddInsuranceModalViewedPreBooking(source?: AppointmentAnalyticsProperty) {
    return this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule,
      source,
    });
  }

  trackPendingInsuranceModalViewed() {
    return this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.PendingInsuranceModalModule,
      source: AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule,
    });
  }

  trackPendingInsuranceModalCtaClicked(useNewEvent = false) {
    return this.trackWithDefaultProperties(useNewEvent ? MP_EVENT_CONTINUE_CLICKED : MP_EVENT_REVIEW_BOOKING_CLICKED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.PendingInsuranceModalModule,
    });
  }

  trackAddInsuranceModalDismissedPreBooking() {
    this.trackBackClicked(AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule);
  }

  trackAddInsuranceActionDeferred() {
    this.trackActionDeferred(AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule);
  }

  trackAddOrReplacePaymentMethodClicked() {
    return this.trackWithDefaultProperties(MP_EVENT_ADD_OR_REPLACE_PAYMENT_METHOD_CLICKED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentSelectedPage,
    });
  }

  trackPaymentCaptureM3ExperimentVariationAssigned(variant: FeatureFlagVariants) {
    return this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule,
      experiment_name: FeatureFlags.PAYMENT_CAPTURE_MILESTONE_3,
      experiment_variation_name: variant,
    });
  }

  trackPaymentCaptureM3ExperimentVariationAssignedReview(variant: FeatureFlagVariants) {
    return this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AppointmentSelectedPage,
      experiment_name: FeatureFlags.PAYMENT_CAPTURE_MILESTONE_3,
      experiment_variation_name: variant,
    });
  }

  trackInsuranceCarrierDropdownSearchExperimentVariationAssigned(variant: FeatureFlagVariantsWithOn) {
    return this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule,
      experiment_name: FeatureFlags.INSURANCE_CAPTURE_CARRIER_DROPDOWN_SEARCH,
      experiment_variation_name: variant,
    });
  }

  trackInsuranceCarrierSelected(carrierId: string, carrierName: string) {
    return this.trackWithDefaultProperties(MP_EVENT_INSURANCE_CARRIER_SELECTED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule,
      carrier_id: carrierId,
      carrier_name: carrierName,
    });
  }

  private trackActionDeferred(module: AppointmentAnalyticsProperty) {
    return this.trackWithDefaultProperties(MP_EVENT_ACTION_DEFERRED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module,
    });
  }

  private trackBackClicked(module: AppointmentAnalyticsProperty) {
    return this.trackWithDefaultProperties(MP_EVENT_BACK_CLICKED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module,
    });
  }

  private trackSelfPayClicked(module: AppointmentAnalyticsProperty) {
    return this.trackWithDefaultProperties(MP_EVENT_SELF_PAY_CLICKED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module,
    });
  }

  private trackPaymentCaptureM3ExperimentVariationAssignedInventory() {
    return forkJoin([
      this.paymentCaptureService.getPaymentCaptureFlagM3$().pipe(take(1)),
      this.paymentCaptureService.getPaymentMethods$().pipe(take(1)),
    ]).pipe(
      switchMap(([paymentCaptureFlagM3, paymentMethods]) => {
        const hasDefaultCopayMethod = paymentMethods.some(method => method.isDefaultCopay);
        return this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, {
          flow: AppointmentAnalyticsProperty.BookingFlow,
          module: AppointmentAnalyticsProperty.InventoryPageModule,
          experiment_name: FeatureFlags.PAYMENT_CAPTURE_MILESTONE_3,
          experiment_variation_name: paymentCaptureFlagM3,
          payment_method_count: paymentMethods.length,
          has_default_copay_method: hasDefaultCopayMethod,
        });
      }),
    );
  }

  private trackInsuranceCaptureRequiredExperimentVariationAssigned() {
    return this.insuranceCaptureService.insuranceCaptureRequiredFlag$().pipe(
      take(1),
      switchMap(insuranceCaptureRequired =>
        this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, {
          flow: AppointmentAnalyticsProperty.BookingFlow,
          module: AppointmentAnalyticsProperty.InventoryPageModule,
          experiment_name: FeatureFlags.INSURANCE_CAPTURE_REQUIRED,
          experiment_variation_name: insuranceCaptureRequired,
        }),
      ),
    );
  }

  private trackInventorySelectedWithExperiment(baseProperties: any, isRemote: boolean): Observable<any> {
    return this.insuranceCaptureService.insuranceCaptureFlowAfterInventorySelectionValues$(true, isRemote).pipe(
      map(({ variant, primaryInsuranceStatus }) => ({
        ...baseProperties,
        experiment_name: FeatureFlags.INSURANCE_CAPTURE_WEB_FULL_PAGE,
        experiment_variation_name: variant,
        insurance_status: primaryInsuranceStatus,
      })),
      switchMap(properties => this.trackWithBookingAndSearchStates(MP_EVENT_APPOINTMENT_SELECTED, properties)),
    );
  }

  private trackLaunchDarklyAppointmentBooked(appointmentTypeId: number) {
    this.trackWithLaunchDarkly({
      key: 'Any Appointment Booked',
      data: { isModuleShown: this.searchState?.trackableProperties?.is_widget_shown },
    });
    if (appointmentTypeId === this.searchState?.trackableProperties?.remoteAppointmentTypeId) {
      this.trackWithLaunchDarkly({
        key: 'Remote Visit Appointment Booked',
        data: { isModuleShown: this.searchState?.trackableProperties?.is_widget_shown },
      });
    }
    // BHX Mental Health PCP Experiment Tracking (LD)
    if (this.isMentalHealthPcpExperiment()) {
      this.trackWithLaunchDarkly({ key: 'Patient Booked PCP Mental Health Visit' });
    }

    // Mental Health Topic Routing Feature (LD)
    if (onlyMentalHealthTopicAppointmentTypesFromBhxExperiment.includes(appointmentTypeId)) {
      this.trackWithLaunchDarkly({ key: MENTAL_HEALTH_ONLY_TOPICS_APPOINTMENT_BOOKED });
    }

    // Mental Health Topic Routing Feature (LD)
    if (!fluAndCovidAppointmentTypeIds.includes(appointmentTypeId)) {
      this.trackWithLaunchDarkly({ key: NOT_COVID_OR_FLU_APPOINTMENT_BOOKED });
    }

    // Mental Health Topic Routing Feature (LD)
    if (mentalHealthAppointmentTypes.includes(appointmentTypeId)) {
      this.trackWithLaunchDarkly({ key: MENTAL_HEALTH_APPOINTMENT_BOOKED });
    }
  }

  private trackLaunchDarklyDeepLinkInventoryPageViewed() {
    // BHX Mental Health PCP Experiment Tracking (LD)
    if (this.isMentalHealthPcpExperiment()) {
      this.trackWithLaunchDarkly({ key: 'Member Appointment Inventory Page viewed' });
    }
  }

  private isMentalHealthPcpExperiment() {
    return (
      this.searchState &&
      this.searchState.getBookingSource() === AppointmentBookingSource.GetCareDeepLinkFlow &&
      this.searchState.appointmentType.id.toString() === MENTAL_HEALTH_VISIT_APPT_TYPE_ID
    );
  }

  private appointmentTypeDisplayName$(appointmentId: string): Observable<string> {
    return this.appointmentAnalyticsTypeInfoGraphQLService
      .fetch({ id: appointmentId })
      .pipe(map(result => result.data.appointment.appointmentType.displayName));
  }

  trackAppointmentToggleExperimentVariationAssigned(status: string) {
    const experimentContext = {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.InventoryPageModule,
      experiment_name: FeatureFlags.APPOINTMENT_VISIT_FILTER_TOGGLE,
      experiment_variation_name: status,
    };

    this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, experimentContext);
  }

  trackScrollableInventoryExperimentVariationAssigned() {
    this.launchDarklyService
      .featureFlag$<FeatureFlagVariants>(FeatureFlags.SCROLLABLE_APPOINTMENT_INVENTORY, FeatureFlagVariants.OFF)
      .pipe(
        filter(flag => flag !== FeatureFlagVariants.OFF),
        map(flag => ({
          flow: AppointmentAnalyticsProperty.BookingFlow,
          module: AppointmentAnalyticsProperty.InventoryPageModule,
          experiment_name: FeatureFlags.SCROLLABLE_APPOINTMENT_INVENTORY,
          experiment_variation_name: flag,
        })),
        tap(context => this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, context)),
        take(1),
      )
      .subscribe();
  }

  trackScrollableInventoryModalDisplayed() {
    const mpContext = {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.ScrollableInventory,
    };

    this.trackWithDefaultProperties(MP_EVENT_MODAL_DISPLAYED, mpContext);
  }

  trackAppointmentTypeSelectionPageViewed({ appointmentTypeCount }: { appointmentTypeCount: number }) {
    this.trackWithBookingAndSearchStates(MP_EVENT_PAGE_VIEWED, {
      flow: FLOW_GET_CARE,
      module: 'Appointment Type Selection Page',
      appointment_type_count: appointmentTypeCount,
    });
  }

  trackStructuredVisitReasonExperimentVariationAssigned({
    experimentName,
    experimentVariationName,
    module,
    topicName,
  }: {
    experimentName: string;
    experimentVariationName: string;
    module: string;
    topicName?: string;
  }) {
    const properties: any = {
      experiment_name: experimentName,
      experiment_variation_name: experimentVariationName,
      flow: FLOW_GET_CARE,
      module: module,
      topic_name: topicName,
    };

    this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, properties);
  }

  trackSelfPayEstimatesExperimentVariationAssigned(variant: FeatureFlagVariants) {
    return this.trackWithDefaultProperties(EXPERIMENT_VARIATION_ASSIGNED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.SelfPayModalModule,
      experiment_name: FeatureFlags.SELF_PAY_ESTIMATES,
      experiment_variation_name: variant,
    });
  }

  trackSelfPayEstimateDisplayed(module: AppointmentAnalyticsProperty) {
    return this.trackWithDefaultProperties(MP_EVENT_SELF_PAY_ESTIMATE_DISPLAYED, {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: module,
    });
  }
}
